import ArticleLink from '@/components/feature/ArticleLink';
import { analysisModules, indicatorReadings } from '@/mocks/home';
import CandleChart from '@/components/charts/CandleChart';
import ScrollReveal from '@/components/base/ScrollReveal';
import SectionHeading from '@/components/base/SectionHeading';

const modulePaths = ['/price-action', '/technical-indicators', '/support-resistance'];

export default function AnalysisSection() {
  return (
    <section className="border-y border-background-200 bg-background-100/60">
      <div className="mx-auto w-full max-w-7xl px-4 py-16 md:px-6 md:py-20 lg:px-10">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-[1fr_1.15fr] lg:gap-16">
          <div>
            <SectionHeading
              eyebrow="การวิเคราะห์"
              title="วิเคราะห์ Forex อย่างไร?"
              subtitle="เรียนรู้เครื่องมือวิเคราะห์หลัก เพื่อช่วยตัดสินใจก่อนเปิดสถานะอย่างมีเหตุผล"
            />

            <div className="mt-10 flex flex-col gap-4">
              {analysisModules.map((module, index) => (
                <ScrollReveal key={module.title} delay={index * 80}>
                  <ArticleLink
                    to={modulePaths[index]}
                    className="group flex items-start gap-5 rounded-xl border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300"
                  >
                    <span className="font-num text-2xl font-semibold text-primary-600">0{index + 1}</span>
                    <div>
                      <h3 className="font-heading text-lg font-semibold text-foreground-950">{module.title}</h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{module.desc}</p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {module.tags.map((tag) => (
                          <span
                            key={tag}
                            className="rounded-full bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-900"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </ArticleLink>
                </ScrollReveal>
              ))}
            </div>
          </div>

          <ScrollReveal delay={120}>
            <div className="flex h-full flex-col rounded-xl border border-background-200 bg-background-50 p-6">
              <div className="flex items-center justify-between">
                <span className="font-num text-sm font-medium text-foreground-700">EUR/USD · ตัวอย่างกราฟ</span>
                <span className="flex items-center gap-2 rounded-full bg-accent-100 px-2.5 py-1 text-xs font-medium text-accent-800">
                  <i className="ri-line-chart-line text-sm" />
                  Trend: ขึ้น
                </span>
              </div>

              <div className="mt-6 flex-1">
                <div className="relative h-64 w-full rounded-lg border border-background-200 bg-background-100/50 p-4">
                  <div className="absolute inset-x-4 top-16 border-t border-dashed border-accent-300" />
                  <span className="absolute right-4 top-14 rounded bg-accent-100 px-2 py-0.5 text-[10px] font-medium text-accent-800">
                    แนวต้าน
                  </span>
                  <div className="absolute inset-x-4 bottom-20 border-t border-dashed border-primary-300" />
                  <span className="absolute right-4 bottom-[74px] rounded bg-primary-100 px-2 py-0.5 text-[10px] font-medium text-primary-700">
                    แนวรับ
                  </span>
                  <div className="flex h-full items-end">
                    <CandleChart />
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-4 text-xs text-foreground-600">
                  <span className="flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-sm bg-secondary-500" />
                    ราคาขึ้น
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-sm bg-accent-500" />
                    ราคาลง
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-sm bg-background-300" />
                    แนวรับ / แนวต้าน
                  </span>
                </div>

                <div className="mt-5 border-t border-background-200 pt-4">
                  <div className="flex items-center justify-between">
                    <p className="flex items-center gap-2 text-xs font-medium text-foreground-700">
                      <i className="ri-speed-up-line text-base text-primary-600" />
                      สัญญาณตัวชี้วัด
                    </p>
                    <span className="text-[11px] text-foreground-500">ตัวอย่างการอ่านค่า</span>
                  </div>
                  <ul className="mt-3 flex flex-col divide-y divide-background-200">
                    {indicatorReadings.map((item) => {
                      const isUp = item.trend === 'up';
                      return (
                        <li key={item.label} className="flex items-center justify-between gap-3 py-2.5">
                          <div className="flex min-w-0 items-center gap-2.5">
                            <span
                              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${
                                isUp ? 'bg-secondary-100 text-secondary-800' : 'bg-accent-100 text-accent-800'
                              }`}
                            >
                              <i className={`${isUp ? 'ri-arrow-up-line' : 'ri-arrow-down-line'} text-sm`} />
                            </span>
                            <div className="min-w-0">
                              <p className="font-num text-sm font-medium text-foreground-900">{item.label}</p>
                              <p className="truncate text-xs text-foreground-500">{item.note}</p>
                            </div>
                          </div>
                          <span
                            className={`font-num text-sm font-semibold ${isUp ? 'text-secondary-700' : 'text-accent-700'}`}
                          >
                            {item.value}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}